import { createSoftwareAppStructuredData, SOURCEDIN_SOFTWARE_DATA } from './software-app'

/**
 * Validates SoftwareApplication structured data against Google's requirements
 */
export function validateSoftwareAppStructuredData() {
  const data = createSoftwareAppStructuredData(SOURCEDIN_SOFTWARE_DATA)
  
  const errors: string[] = []
  const warnings: string[] = []
  
  // Required properties validation
  if (!data.name) {
    errors.push('Missing required property: name')
  }
  
  if (!data.description) {
    errors.push('Missing required property: description')
  }
  
  if (!data.url) {
    errors.push('Missing required property: url')
  }
  
  if (!data.applicationCategory) {
    errors.push('Missing required property: applicationCategory')
  }
  
  if (!data.operatingSystem || !Array.isArray(data.operatingSystem) || data.operatingSystem.length === 0) {
    errors.push('Missing required property: operatingSystem')
  }
  
  if (!data.offers || !data.offers.price) {
    errors.push('Missing required property: offers.price')
  }
  
  // Rating or review validation (at least one is required)
  if (!(data as any).aggregateRating && !(data as any).review) {
    errors.push('Missing required property: either aggregateRating or review must be provided')
  }
  
  // Validate aggregateRating if present
  if ((data as any).aggregateRating) {
    if (!(data as any).aggregateRating.ratingValue || (data as any).aggregateRating.ratingValue < 1 || (data as any).aggregateRating.ratingValue > 5) {
      errors.push('Invalid aggregateRating.ratingValue: must be between 1 and 5')
    }
    if (!(data as any).aggregateRating.reviewCount || (data as any).aggregateRating.reviewCount < 1) {
      errors.push('Invalid aggregateRating.reviewCount: must be at least 1')
    }
  }
  
  // Validate review if present
  if ((data as any).review) {
    if (!(data as any).review.reviewRating || !(data as any).review.reviewRating.ratingValue) {
      errors.push('Invalid review.reviewRating.ratingValue')
    }
    if (!(data as any).review.author || !(data as any).review.author.name) {
      errors.push('Invalid review.author.name')
    }
    if (!(data as any).review.reviewBody) {
      errors.push('Invalid review.reviewBody')
    }
  }
  
  // Validate applicationCategory
  const validCategories = [
    'BusinessApplication', 'GameApplication', 'SocialNetworkingApplication',
    'TravelApplication', 'ShoppingApplication', 'SportsApplication',
    'LifestyleApplication', 'DesignApplication', 'DeveloperApplication',
    'DriverApplication', 'EducationalApplication', 'HealthApplication',
    'FinanceApplication', 'SecurityApplication', 'BrowserApplication',
    'CommunicationApplication', 'DesktopEnhancementApplication',
    'EntertainmentApplication', 'MultimediaApplication', 'HomeApplication',
    'UtilitiesApplication', 'ReferenceApplication'
  ]
  
  if (data.applicationCategory && !validCategories.includes(data.applicationCategory)) {
    warnings.push(`Unsupported applicationCategory: ${data.applicationCategory}`)
  }
  
  // Validate price
  if (data.offers && typeof data.offers.price !== 'number') {
    errors.push('Invalid offers.price: must be a number')
  }
  
  // Validate currency
  if (data.offers && data.offers.price > 0 && !data.offers.priceCurrency) {
    warnings.push('Missing offers.priceCurrency for paid app')
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    data
  }
}

/**
 * Generates a test URL for Google's Rich Results Test
 */
export function generateRichResultsTestUrl(pageUrl: string) {
  return `https://search.google.com/test/rich-results?url=${encodeURIComponent(pageUrl)}`
}

/**
 * Logs validation results to console for debugging
 */
export function logValidationResults() {
  const validation = validateSoftwareAppStructuredData()
  
  console.group('🔍 SoftwareApplication Structured Data Validation')
  
  if (validation.isValid) {
    console.log('✅ Validation passed!')
  } else {
    console.error('❌ Validation failed!')
  }
  
  if (validation.errors.length > 0) {
    console.group('🚨 Errors:')
    validation.errors.forEach(error => console.error(`- ${error}`))
    console.groupEnd()
  }
  
  if (validation.warnings.length > 0) {
    console.group('⚠️ Warnings:')
    validation.warnings.forEach(warning => console.warn(`- ${warning}`))
    console.groupEnd()
  }
  
  console.log('📊 Generated structured data:', validation.data)
  console.groupEnd()
  
  return validation
}
